import { ApiErrorHandler } from "api-client";
import { bookingsService } from "../../../shared/services/bookings.service";

export type UnassignCarrierNotificationInput = {
  variant: "error" | "success";
  title: string;
  description: string;
};

export type ExecuteUnassignCarrierOptions = {
  bookingId: number | null;
  assignmentId: number;
  /** Display name for the success toast (FR-AV149-018). */
  carrierName: string;
  /** Booking number for the success toast (FR-AV149-018). */
  bookingNumber: string;
  showNotification: (input: UnassignCarrierNotificationInput) => void;
  refreshDetail: () => Promise<void>;
  onListRefresh: () => void;
};

const UNASSIGN_FAILED_FALLBACK = "Failed to unassign carrier.";

function buildUnassignSuccessDescription(carrierName: string, bookingNumber: string): string {
  const name = carrierName.trim() || "Carrier";
  const booking = bookingNumber.trim();
  if (booking) {
    return `${name} was unassigned from booking ${booking} successfully.`;
  }
  return `${name} was unassigned successfully.`;
}

/** Shared unassign-carrier submit (FR-AV149-018, NFR-AV149-006). Re-throws after error toast. */
export async function executeUnassignCarrier(
  options: ExecuteUnassignCarrierOptions,
): Promise<void> {
  const {
    bookingId,
    assignmentId,
    carrierName,
    bookingNumber,
    showNotification,
    refreshDetail,
    onListRefresh,
  } = options;

  if (bookingId == null) {
    throw new Error("Invalid booking id.");
  }

  try {
    const res = await bookingsService.unassignCarrier({
      bookingId,
      assignmentId,
    });
    if (res.statusCode < 200 || res.statusCode >= 300) {
      throw new Error(res.message?.trim() || UNASSIGN_FAILED_FALLBACK);
    }
    showNotification({
      variant: "success",
      title: "Unassigned successfully",
      description: buildUnassignSuccessDescription(carrierName, bookingNumber),
    });
    await refreshDetail();
    onListRefresh();
  } catch (e: unknown) {
    const message = ApiErrorHandler.extractAxiosError(e, UNASSIGN_FAILED_FALLBACK);
    showNotification({
      variant: "error",
      title: "Unassign failed",
      description: message,
    });
    throw new Error(message);
  }
}
